/**
 * Lead-form anonymous-prefill storage.
 *
 * Mirrors `name` / `phone` / `email` from a successful submit into
 * localStorage so the next time the same browser opens any LeadForm
 * surface, those three fields land pre-filled. Reduces friction by
 * roughly three input rounds, which compounds quickly given 90%+ of
 * SpotMe traffic is mobile (keyboard tax is brutal).
 *
 * Authenticated users get a richer prefill source straight from
 * `useAuthProvider().currentUser` (firstname / lastname / phoneNumber /
 * email) — the localStorage entry is only consulted for anonymous
 * users. Server-side state is always authoritative when signed in.
 *
 * SSR-safe: every reader / writer guards on `typeof window`. No data
 * leaves the browser — this is purely a UX shortcut, not a remote
 * cache. TTL keeps stale entries from sticking around forever (30
 * days; long enough to cover a typical decision cycle on a B2B lead,
 * short enough that a borrowed device eventually clears).
 */

const KEY = 'spotme_lead_prefill_v1'
const TTL_MS = 30 * 24 * 60 * 60 * 1000

export type LeadFormContactPrefill = {
  name: string
  phone: string
  email: string
}

type StoredPrefill = LeadFormContactPrefill & {
  savedAt: number
}

function isStoredPrefill(value: unknown): value is StoredPrefill {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.savedAt === 'number' &&
    typeof v.name === 'string' &&
    typeof v.phone === 'string' &&
    typeof v.email === 'string'
  )
}

export function readLeadContactPrefill(): LeadFormContactPrefill | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isStoredPrefill(parsed)) return null
    if (Date.now() - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(KEY)
      return null
    }
    return { name: parsed.name, phone: parsed.phone, email: parsed.email }
  } catch {
    // Malformed JSON, private-browsing exception — treat as no prefill.
    return null
  }
}

export function writeLeadContactPrefill(contact: LeadFormContactPrefill): void {
  if (typeof window === 'undefined') return
  if (!contact.name && !contact.phone && !contact.email) return
  try {
    const payload: StoredPrefill = { ...contact, savedAt: Date.now() }
    window.localStorage.setItem(KEY, JSON.stringify(payload))
  } catch {
    // Quota exceeded, private browsing — silently no-op so the form
    // submit isn't affected.
  }
}

export function clearLeadContactPrefill(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // ignore
  }
}

// --- Per-listing submission dedupe ----------------------------------------
//
// When a user successfully submits a lead, we mark the `(leadSource ×
// configurationId)` pair so the next time they open the same form
// surface within the TTL window, we render a `RevisitScreen` instead of
// the input fields. Avoids double submits, gives a clear "we already
// have your request" UX, and offers a direct WA escape hatch for
// follow-up. UX-only — server-side idempotency is the real defense.
//
// Stored as a SINGLE map under one localStorage key (vs. one key per
// listing) so we can age out stale entries in bulk on every read and
// avoid littering `localStorage` over time. Entries older than
// `SUBMISSION_TTL_MS` are silently dropped at read time.

const SUBMISSIONS_KEY = 'spotme_lead_submissions_v1'
const SUBMISSION_TTL_MS = 7 * 24 * 60 * 60 * 1000

export type LeadSubmissionKey = string

type StoredSubmission = {
  submittedAt: number
}

type SubmissionMap = Record<string, StoredSubmission>

/**
 * Compose a stable dedupe key from the entry surface + listing config.
 *   - listing modals → `'listing_broker_modal:cfg_abc123'`
 *   - flotillas hero (no listing) → `'flotillas_full:none'`
 * Same listing reopened gets the SAME key; a different listing gets a
 * different key (and a fresh form).
 */
export function buildLeadKey(args: {
  leadSource: string
  configurationId?: string | null
}): LeadSubmissionKey {
  return `${args.leadSource}:${args.configurationId ?? 'none'}`
}

function readSubmissionMap(): SubmissionMap {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(SUBMISSIONS_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {}
    }
    const now = Date.now()
    const out: SubmissionMap = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as Record<string, unknown>).submittedAt === 'number'
      ) {
        const submittedAt = (value as { submittedAt: number }).submittedAt
        if (now - submittedAt < SUBMISSION_TTL_MS) {
          out[key] = { submittedAt }
        }
      }
    }
    return out
  } catch {
    return {}
  }
}

function writeSubmissionMap(map: SubmissionMap): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SUBMISSIONS_KEY, JSON.stringify(map))
  } catch {
    // Quota / private browsing — silently no-op.
  }
}

export function markLeadSubmitted(leadKey: LeadSubmissionKey): void {
  if (typeof window === 'undefined') return
  const map = readSubmissionMap()
  map[leadKey] = { submittedAt: Date.now() }
  writeSubmissionMap(map)
}

export function getLastLeadSubmission(
  leadKey: LeadSubmissionKey
): StoredSubmission | null {
  if (typeof window === 'undefined') return null
  const map = readSubmissionMap()
  return map[leadKey] ?? null
}

export function clearLeadSubmission(leadKey: LeadSubmissionKey): void {
  if (typeof window === 'undefined') return
  const map = readSubmissionMap()
  if (!(leadKey in map)) return
  delete map[leadKey]
  writeSubmissionMap(map)
}
